import { getAccessToken } from "@/services/api/authSession"
import {
  fetchWithConnectivity,
  isAbortError,
} from "@/services/api/connectivity"
import type {
  AnchorOut,
  ApSettingsOut,
  BuildHierarchyOut,
  ChangeEvent,
  CommitOut,
  ConnectedPrimitive,
  DetectConnectedOut,
  DetectOut,
  DownloadColumn,
  ExportExtractOut,
  ExtractExplicitOut,
  GroupNode,
  GroupTree,
  ImplicitExtractionOut,
  ImportEvent,
  ParsedDocument,
  PendingDocumentOut,
  PreviewOut,
  PrimitiveDef,
  PrimitiveHistoryGroup,
  QmsRecord,
  ReachablePrimitive,
  RecordCreateIn,
  RecordsPage,
  RecordUpdateIn,
  RelationPreview,
  RollbackResult,
  RollupCellValue,
  Scope,
  ScopeListItem,
  SessionEventOut,
  SessionOut,
  SessionRollbackResult,
  TransitiveCounts,
} from "../types"

const BASE =
  (import.meta.env.VITE_APP_API_BASE_URL ?? "http://localhost:8000/api/v1") +
  "/qms"

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    return { Authorization: `Bearer ${await getAccessToken()}` }
  } catch (error) {
    // A request whose session has ended must not go out at all.
    if (isAbortError(error)) throw error
    // fall through — let the request fail with 401 naturally
  }
  return {}
}

function currentAcceptLanguage(): string {
  const lang = localStorage.getItem("language")?.toLowerCase()
  if (lang === "en" || lang === "de" || lang === "ja") return lang
  return "de"
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const auth = await getAuthHeaders()
  const res = await fetchWithConnectivity(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": currentAcceptLanguage(),
      ...auth,
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`API ${res.status}: ${text}`)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

// ---------------------------------------------------------------------------
// Scopes
// ---------------------------------------------------------------------------

export const api = {
  // Scopes
  listScopes: () => request<ScopeListItem[]>("/scopes"),
  getScope: (id: string) => request<Scope>(`/scopes/${id}`),
  scopeRecordCounts: () => request<Record<string, number>>("/scopes/counts"),
  setPrimitiveVisibility: (
    scopeId: string,
    primId: string,
    isVisible: boolean,
  ) =>
    request(`/scopes/${scopeId}/primitives/${primId}/visibility`, {
      method: "PATCH",
      body: JSON.stringify({ is_visible: isVisible }),
    }),
  updatePrimitive: (
    scopeId: string,
    primId: string,
    payload: {
      title?: string
      description?: string
      is_visible?: boolean
      config?: Record<string, unknown>
    },
  ) =>
    request(`/scopes/${scopeId}/primitives/${primId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  reorderPrimitives: (scopeId: string, primitiveIds: string[]) =>
    request(`/scopes/${scopeId}/primitives/reorder`, {
      method: "PUT",
      body: JSON.stringify({ primitive_ids: primitiveIds }),
    }),
  createMapping: (
    scopeId: string,
    primId: string,
    payload: { title: string; source_primitive_id?: string | null },
  ) =>
    request<PrimitiveDef>(
      `/scopes/${scopeId}/primitives/${primId}/create-mapping`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),
  deleteMapping: (scopeId: string, primId: string) =>
    request<PrimitiveDef>(`/scopes/${scopeId}/primitives/${primId}/mapping`, {
      method: "DELETE",
    }),
  createPrimitive: (
    scopeId: string,
    payload: { title: string; type: string; formula?: string },
  ) =>
    request<PrimitiveDef>(`/scopes/${scopeId}/primitives`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  deletePrimitive: (scopeId: string, primId: string) =>
    request<void>(`/scopes/${scopeId}/primitives/${primId}`, {
      method: "DELETE",
    }),
  setScopeHidden: (scopeId: string, isHidden: boolean) =>
    request<ScopeListItem>(`/scopes/${scopeId}/hidden`, {
      method: "PATCH",
      body: JSON.stringify({ is_hidden: isHidden }),
    }),
  bulkSetHiddenScopes: (hiddenScopeIds: string[]) =>
    request<ScopeListItem[]>("/scopes/hidden/bulk", {
      method: "PUT",
      body: JSON.stringify({ hidden_scope_ids: hiddenScopeIds }),
    }),

  // Hierarchy info (for cross-scope relation level filtering)
  getHierarchyInfo: (scopeId: string, fromScopeId?: string) => {
    const params = fromScopeId
      ? `?from_scope_id=${encodeURIComponent(fromScopeId)}`
      : ""
    return request<{
      scope_id: string
      primitive_id: string
      primitive_name: string
      primitive_title: string
      level_labels: Record<string, string>
      level_filter_options: { level: number; label: string; count: number }[]
      default_level: string
    }>(`/scopes/${scopeId}/hierarchy-info${params}`)
  },

  // Records
  listRecords: (
    scopeId: string,
    relationLevelFilters?: Record<string, string>,
  ) => {
    const params =
      relationLevelFilters && Object.keys(relationLevelFilters).length > 0
        ? `?relation_level_filters=${encodeURIComponent(JSON.stringify(relationLevelFilters))}`
        : ""
    return request<RecordsPage>(`/scopes/${scopeId}/records${params}`)
  },
  createRecord: (scopeId: string, payload: RecordCreateIn) =>
    request<QmsRecord>(`/scopes/${scopeId}/records`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getRecord: (recordId: string) => request<QmsRecord>(`/records/${recordId}`),
  updateRecord: (recordId: string, payload: RecordUpdateIn) =>
    request<QmsRecord>(`/records/${recordId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  deleteRecord: (recordId: string) =>
    request<void>(`/records/${recordId}`, { method: "DELETE" }),

  // History
  getHistory: (recordId: string) =>
    request<PrimitiveHistoryGroup[]>(`/records/${recordId}/history`),

  // Relations
  getRelationPreview: (
    recordId: string,
    primName: string,
    relationLevelFilter?: string,
  ) => {
    const params = relationLevelFilter
      ? `?relation_level_filter=${encodeURIComponent(relationLevelFilter)}`
      : ""
    return request<RelationPreview>(
      `/records/${recordId}/relations/${primName}${params}`,
    )
  },
  addRelation: (recordId: string, primName: string, targetId: string) =>
    request<void>(`/records/${recordId}/relations/${primName}/${targetId}`, {
      method: "POST",
    }),
  removeRelation: (recordId: string, primName: string, targetId: string) =>
    request<void>(`/records/${recordId}/relations/${primName}/${targetId}`, {
      method: "DELETE",
    }),
  updateMappedId: (relationId: string, mappedId: string | null) =>
    request<{ id: string; mapped_id: string | null }>(
      `/relations/${relationId}/mapped-id`,
      {
        method: "PATCH",
        body: JSON.stringify({ mapped_id: mappedId }),
      },
    ),

  // Filtered records (graph traversal)
  listFilteredRecords: (
    scopeId: string,
    originScope: string,
    pks: string[],
    relationLevelFilters?: Record<string, string>,
  ) => {
    let url = `/scopes/${scopeId}/records/filtered?origin_scope=${encodeURIComponent(originScope)}&pks=${encodeURIComponent(pks.join(","))}`
    if (relationLevelFilters && Object.keys(relationLevelFilters).length > 0) {
      url += `&relation_level_filters=${encodeURIComponent(JSON.stringify(relationLevelFilters))}`
    }
    return request<RecordsPage>(url)
  },

  // Transitive relation counts (multi-hop)
  getTransitiveCounts: (
    scopeId: string,
    relationLevelFilters?: Record<string, string>,
  ) => {
    const params = new URLSearchParams()
    if (relationLevelFilters && Object.keys(relationLevelFilters).length > 0) {
      params.set("relation_level_filters", JSON.stringify(relationLevelFilters))
    }
    const qs = params.toString()
    return request<TransitiveCounts>(
      `/scopes/${scopeId}/transitive-counts${qs ? `?${qs}` : ""}`,
    )
  },

  // Directional-cascade staleness: returns record IDs that are effectively
  // stale given the freshness date and scope ranks (supplier → ... → customer).
  getEffectiveStaleness: (
    freshnessDate: string,
    recordsByScope: Record<string, string[]>,
  ) =>
    request<{ stale_record_ids: string[] }>("/scopes/effective-staleness", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        freshness_date: freshnessDate,
        records_by_scope: recordsByScope,
      }),
    }),

  // Admin
  resetDb: () =>
    request<{ status: string }>("/admin/reset", { method: "POST" }),

  resetDemo: () =>
    request<{
      status: string
      cleared: Record<string, number>
      evaluator: { created: number; superseded: number; flagged: string[] }
    }>("/admin/reset-demo", { method: "POST" }),

  monitorSupplierCascade: (body: {
    supplier_record_id: string
    direction: "stale" | "refresh"
  }) =>
    request<{ ok: true; affected_component_count: number }>(
      "/admin/monitor-supplier-cascade",
      { method: "POST", body: JSON.stringify(body) },
    ),
  listImportEvents: () => request<ImportEvent[]>("/admin/import-events"),
  changeTimeline: () => request<ChangeEvent[]>("/admin/change-timeline"),
  rollbackImportEvent: (eventId: string, force?: boolean) =>
    request<RollbackResult>(
      `/admin/import-events/${eventId}/rollback${force ? "?force=true" : ""}`,
      { method: "POST" },
    ),
  rollbackToTimestamp: (targetTimestamp: string) =>
    request<RollbackResult>("/admin/rollback-to-timestamp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ target_timestamp: targetTimestamp }),
    }),

  // Ingestion pipeline
  ingestUpload: async (scopeId: string, file: File) => {
    const form = new FormData()
    form.append("file", file)
    const headers = await getAuthHeaders()
    const res = await fetch(`${BASE}/scopes/${scopeId}/ingest/upload`, {
      method: "POST",
      headers,
      body: form,
    })
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.json() as Promise<ParsedDocument>
  },

  ingestAnchor: (
    scopeId: string,
    payload: {
      file_type: string
      rows?: string[][]
      raw_text?: string
      headers?: string[]
      pk_col_override?: number | null
      anchor_mode?: "pk" | "mapping"
      mapping_primitive_id?: string | null
      mapping_col_override?: number | null
    },
  ) =>
    request<AnchorOut>(`/scopes/${scopeId}/ingest/anchor`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  ingestDetect: (
    scopeId: string,
    payload: {
      file_type: string
      headers?: string[]
      sample_rows?: string[][]
    },
  ) =>
    request<DetectOut>(`/scopes/${scopeId}/ingest/detect`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  ingestExtractExplicit: (
    scopeId: string,
    payload: {
      mappings: {
        primitive_id: string
        primitive_scope_id: string
        column_index: number
        is_mapping_title?: boolean
      }[]
      anchored_records: {
        record_id: string
        row_index: number | null
        additional_row_indices?: number[]
      }[]
      rows: string[][]
    },
  ) =>
    request<ExtractExplicitOut>(`/scopes/${scopeId}/ingest/extract-explicit`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  ingestExtractImplicit: (
    scopeId: string,
    payload: {
      primitive_id: string
      primitive_title: string
      primitive_description: string
      primitive_type: string
      scope_id: string
      anchored_record_id: string
      document_text: string
      existing_context: Record<string, string>
      instruction?: string
    },
  ) =>
    request<ImplicitExtractionOut>(
      `/scopes/${scopeId}/ingest/extract-implicit`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),

  // Group hierarchy
  getGroupTree: (scopeId: string, primitiveId: string) =>
    request<GroupTree>(`/scopes/${scopeId}/groups/${primitiveId}/tree`),

  listGroupNodes: (scopeId: string, primitiveId: string) =>
    request<GroupNode[]>(`/scopes/${scopeId}/groups/${primitiveId}/nodes`),

  buildHierarchy: (
    scopeId: string,
    primitiveId: string,
    payload: {
      mode: "horizontal" | "flat" | "parent_ref" | "pk_prefix"
      rows: string[][]
      headers?: string[]
      horizontal?: { primitive_id: string; level_columns: number[] }
      flat?: {
        primitive_id: string
        name_column: number
        parent_column: number
      }
      parent_ref?: {
        primitive_id: string
        pk_column: number
        ref_columns: number[]
        name_column: number
        level_labels: string[]
      }
      pk_prefix?: {
        primitive_id: string
        pk_column: number
        separator: string
        name_column: number
        level_label_column?: number | null
        level_labels?: string[]
      }
    },
  ) =>
    request<BuildHierarchyOut>(
      `/scopes/${scopeId}/groups/${primitiveId}/build`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),

  commitHierarchy: (
    scopeId: string,
    primitiveId: string,
    payload: {
      mode: "horizontal" | "flat" | "parent_ref" | "pk_prefix"
      rows: string[][]
      headers?: string[]
      horizontal?: { primitive_id: string; level_columns: number[] }
      flat?: {
        primitive_id: string
        name_column: number
        parent_column: number
      }
      parent_ref?: {
        primitive_id: string
        pk_column: number
        ref_columns: number[]
        name_column: number
        level_labels: string[]
      }
      pk_prefix?: {
        primitive_id: string
        pk_column: number
        separator: string
        name_column: number
        level_label_column?: number | null
        level_labels?: string[]
      }
    },
  ) =>
    request<GroupTree>(
      `/scopes/${scopeId}/groups/${primitiveId}/commit-hierarchy`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),

  ingestCommit: (
    scopeId: string,
    payload: {
      document_name: string
      extractions: {
        record_id: string
        primitive_id: string
        value: string | null
        source: string
        confidence: number | null
        extraction_type: string
        needs_cleaning: boolean
        confirmed_by_user: boolean
        is_new: boolean
        pk_value?: string | null
        mapped_id?: string | null
        action?: string // "add" (default) or "remove" — for relation primitives
      }[]
      history_mode?: string // "all" | "changed_only" | "none"
      touch_record_ids?: string[]
      confirmed_record_ids?: string[] // records confirmed as fresh (row-level freshness)
    },
  ) =>
    request<CommitOut>(`/scopes/${scopeId}/ingest/commit`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  ingestTouch: (
    scopeId: string,
    payload: {
      document_name: string
      history_mode: string
      record_ids?: string[] // if set, only these records are touched
    },
  ) =>
    request<{ touched: number; import_event_id: string }>(
      `/scopes/${scopeId}/ingest/touch`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),

  activeTemplates: () =>
    request<{
      export: Array<{
        id: string
        label: string
        description: string
        format: string
        anchor_scope_ids: string[]
        primitives: { scope_id: string; primitive_name: string }[]
        relations: {
          from_scope_id: string
          relation_primitive_name: string
          to_scope_id: string
        }[]
        status: "ready" | "missing_primitives"
        missing: string[]
      }>
      ingestion: Array<{
        id: string
        label: string
        description: string
        anchor_scope_id: string
        resolutions: unknown[]
        writes: unknown[]
        status: "ready" | "missing_primitives"
        missing: string[]
      }>
    }>("/bom/templates"),

  // ---- Pending documents (importer-AP staging) -------------------------

  pendingDocumentsList: (apId: string, targetRecordId?: string | null) => {
    const q = new URLSearchParams()
    if (targetRecordId !== undefined) {
      q.set("target_record_id", targetRecordId ?? "")
    }
    const qs = q.toString()
    return request<PendingDocumentOut[]>(
      `/ap/${apId}/documents${qs ? `?${qs}` : ""}`,
    )
  },

  pendingDocumentUpload: async (
    apId: string,
    file: File,
    source: "upload" | "webhook" | "email" = "upload",
  ) => {
    const auth = await getAuthHeaders()
    const form = new FormData()
    form.append("file", file)
    form.append("source", source)
    const res = await fetch(`${BASE}/ap/${apId}/documents/upload`, {
      method: "POST",
      body: form,
      headers: { ...auth },
    })
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.json() as Promise<PendingDocumentOut>
  },

  pendingDocumentWebhookSimulate: async (
    apId: string,
    token: string,
    filename: string,
    content: string,
  ) => {
    const auth = await getAuthHeaders()
    // Encode the text payload as base64 so it fits the WebhookUploadIn schema.
    const bytes = new TextEncoder().encode(content)
    let binary = ""
    bytes.forEach((b) => {
      binary += String.fromCharCode(b)
    })
    const file_base64 = btoa(binary)
    const res = await fetch(
      `${BASE}/ap/${apId}/documents/webhook?token=${encodeURIComponent(token)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth },
        body: JSON.stringify({
          filename,
          content_type: "text/plain",
          file_base64,
        }),
      },
    )
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.json() as Promise<PendingDocumentOut>
  },

  pendingDocumentEmailSimulate: async (apId: string, file: File) => {
    const auth = await getAuthHeaders()
    const form = new FormData()
    form.append("file", file)
    const res = await fetch(`${BASE}/ap/${apId}/documents/email`, {
      method: "POST",
      body: form,
      headers: { ...auth },
    })
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.json() as Promise<PendingDocumentOut>
  },

  pendingDocumentReassign: (
    apId: string,
    docId: string,
    targetRecordId: string | null,
  ) =>
    request<PendingDocumentOut>(`/ap/${apId}/documents/${docId}`, {
      method: "PATCH",
      body: JSON.stringify({ target_record_id: targetRecordId }),
    }),

  pendingDocumentDelete: (apId: string, docId: string) =>
    request<void>(`/ap/${apId}/documents/${docId}`, { method: "DELETE" }),

  pendingDocumentConsume: (apId: string, docId: string) =>
    request<void>(`/ap/${apId}/documents/${docId}/consume`, { method: "POST" }),

  pendingDocumentDownloadUrl: (apId: string, docId: string, inline = false) =>
    `${BASE}/ap/${apId}/documents/${docId}/download${inline ? "?inline=true" : ""}`,

  /** Fetch a pending document with auth headers and open it as a blob URL. */
  openDocument: async (apId: string, docId: string): Promise<void> => {
    const auth = await getAuthHeaders()
    const url = `${BASE}/ap/${apId}/documents/${docId}/download?inline=true`
    const res = await fetch(url, { headers: auth })
    if (!res.ok) throw new Error(`${res.status}`)
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    window.open(blobUrl, "_blank", "noopener")
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
  },

  apSettings: (apId: string) => request<ApSettingsOut>(`/ap/${apId}/settings`),

  apSettingsRotateToken: (apId: string) =>
    request<ApSettingsOut>(`/ap/${apId}/settings/rotate-token`, {
      method: "POST",
    }),

  apActivityAppend: (payload: {
    ap_id: string
    kind: string
    summary: string
    metadata?: Record<string, unknown>
  }) =>
    request<{
      id: string
      ap_id: string
      kind: string
      summary: string
      created_at: string
    }>("/ap/activity", { method: "POST", body: JSON.stringify(payload) }),

  apActivityList: (limit?: number) =>
    request<
      Array<{
        id: string
        ap_id: string
        kind: string
        summary: string
        metadata: unknown
        created_at: string
      }>
    >(`/ap/activity${limit != null ? `?limit=${limit}` : ""}`),

  // Legacy bomSetupStart / bomSetupStatus / bomApplySchema / bomImport
  // were removed.  The wizard and AssistantView now go through the session
  // API (sessionCreate + sessionAttachFile + sessionRun + sessionApprove).

  // Export
  exportAvailableColumns: (scopeId: string) =>
    request<ReachablePrimitive[]>(
      `/scopes/${scopeId}/export/available-columns`,
    ),

  exportExtract: (
    scopeId: string,
    payload: {
      mappings: {
        primitive_id: string
        primitive_scope_id: string
        column_index: number
        rollup?: boolean
      }[]
      anchored_records: {
        record_id: string
        row_index: number | null
        additional_row_indices?: number[]
      }[]
      rows: string[][]
      headers?: string[]
    },
  ) =>
    request<ExportExtractOut>(`/scopes/${scopeId}/export/extract`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  exportBuildFile: async (
    scopeId: string,
    payload: {
      headers: string[]
      rows: string[][]
      edits: { row_index: number; column_index: number; new_value: string }[]
      delimiter?: string
      file_name?: string
    },
  ) => {
    const authHeaders = await getAuthHeaders()
    const res = await fetch(`${BASE}/scopes/${scopeId}/export/build-file`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.blob()
  },

  exportPreview: (
    scopeId: string,
    payload: {
      record_ids: string[]
      columns: { primitive_id: string; scope_id: string; rollup?: boolean }[]
    },
  ) =>
    request<PreviewOut>(`/scopes/${scopeId}/export/preview`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  exportDownload: async (
    scopeId: string,
    payload: {
      record_ids: string[]
      columns: DownloadColumn[]
      format?: "csv" | "excel" | "produkt_konformitaet" | "kunden_konformitaet"
      overrides?: Record<string, Record<string, string>>
    },
  ) => {
    const authHeaders = await getAuthHeaders()
    const res = await fetch(`${BASE}/scopes/${scopeId}/export/download`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.blob()
  },

  // --- Record Selection (Select Records) ---

  detectConnected: (
    scopeId: string,
    payload: { file_type: string; headers: string[] },
  ) =>
    request<DetectConnectedOut>(`/scopes/${scopeId}/ingest/detect-connected`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  listConnectedPrimitives: (scopeId: string) =>
    request<ConnectedPrimitive[]>(
      `/scopes/${scopeId}/ingest/connected-primitives`,
    ),

  // --- Record-level drilldown ---

  getReachableScopes: (recordIds: string[]) =>
    request<{ scopes: { scope_id: string; scope_name: string }[] }>(
      "/records/reachable-scopes",
      {
        method: "POST",
        body: JSON.stringify({ record_ids: recordIds }),
      },
    ),

  // --- Rollup ---

  computeRollup: (
    scopeId: string,
    payload: {
      record_ids: string[]
      columns: {
        primitive_id: string
        primitive_scope_id: string
        aggregation: string
        label: string
      }[]
      source_record_ids?: Record<string, string[]>
    },
  ) =>
    request<{ values: RollupCellValue[] }>(
      `/scopes/${scopeId}/rollup/compute`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),

  // Conformity letter
  conformityLetterCustomers: () =>
    request<
      { record_id: string; customer_id: string; customer_name: string }[]
    >("/conformity-letter/customers"),

  conformityLetterData: (
    customerRecordIds: string[],
    overrides?: Record<string, Record<string, string>>,
  ) =>
    request<
      {
        customer_name: string
        customer_id: string
        is_positive: boolean
        total_product_count: number
        svhc_product_count: number
        rows: {
          product_id: string
          product_name: string
          cas_number: string
          substance_name: string
          svhc_gehalt: string
          location_in_component: string
          component_name: string
        }[]
        products: {
          product_id: string
          product_name: string
          rows: {
            product_id: string
            product_name: string
            cas_number: string
            substance_name: string
            svhc_gehalt: string
            location_in_component: string
            component_name: string
          }[]
        }[]
      }[]
    >("/conformity-letter/data", {
      method: "POST",
      body: JSON.stringify({
        customer_record_ids: customerRecordIds,
        overrides,
      }),
    }),

  supplierInquiryData: (
    supplierRecordIds: string[],
    overrides?: Record<string, Record<string, string>>,
  ) =>
    request<
      {
        supplier_id: string
        supplier_name: string
        contact_name: string
        contact_position: string
        contact_location: string
        total_component_count: number
        components_with_svhc_count: number
        components: {
          component_id: string
          component_name: string
          last_declared_svhc_present: string
          last_supplier_info_date: string
          rows: {
            component_id: string
            component_name: string
            cas_number: string
            substance_name: string
            last_declared_svhc_gehalt: string
          }[]
        }[]
      }[]
    >("/conformity-letter/supplier-inquiry-data", {
      method: "POST",
      body: JSON.stringify({
        supplier_record_ids: supplierRecordIds,
        overrides,
      }),
    }),

  conformityProductData: (
    productRecordIds: string[],
    overrides?: Record<string, Record<string, string>>,
  ) =>
    request<
      {
        product_id: string
        product_name: string
        is_positive: boolean
        rows: {
          product_id: string
          product_name: string
          cas_number: string
          substance_name: string
          svhc_gehalt: string
          location_in_component: string
          component_name: string
        }[]
      }[]
    >("/conformity-letter/product-data", {
      method: "POST",
      body: JSON.stringify({ product_record_ids: productRecordIds, overrides }),
    }),

  conformityEschaKundenData: (
    customerRecordIds: string[],
    overrides?: Record<string, Record<string, string>>,
  ) =>
    request<
      {
        customer_record_id: string
        customer_id: string
        products: {
          product_record_id: string
          internal_product_id: string
          fremdartikelnummer: string
          artikelausfuehrung: string
          kupferanteil_artikel_kg: string
          menge_blei: string
          basismenge: string
          gewicht: string
        }[]
      }[]
    >("/conformity-letter/escha-kunden-data", {
      method: "POST",
      body: JSON.stringify({
        customer_record_ids: customerRecordIds,
        overrides,
      }),
    }),

  // ---- Session architecture (see backend sessions package) --------------

  sessionCreate: (customer: string, intent?: string) =>
    request<SessionOut>("/sessions", {
      method: "POST",
      body: JSON.stringify({ customer, intent }),
    }),

  sessionList: (customer?: string, limit = 50) => {
    const q = new URLSearchParams()
    if (customer) q.set("customer", customer)
    q.set("limit", String(limit))
    return request<SessionOut[]>(`/sessions?${q.toString()}`)
  },

  sessionGet: (sessionId: string) =>
    request<SessionOut>(`/sessions/${sessionId}`),

  sessionAttachFile: async (sessionId: string, file: File) => {
    const auth = await getAuthHeaders()
    const form = new FormData()
    form.append("file", file)
    const res = await fetch(`${BASE}/sessions/${sessionId}/files`, {
      method: "POST",
      body: form,
      headers: { ...auth },
    })
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.json() as Promise<SessionOut>
  },

  sessionRun: (sessionId: string, requireApproval?: boolean) => {
    const q =
      requireApproval != null ? `?require_approval=${requireApproval}` : ""
    return request<SessionOut>(`/sessions/${sessionId}/run${q}`, {
      method: "POST",
    })
  },

  sessionApprove: (sessionId: string) =>
    request<SessionOut>(`/sessions/${sessionId}/approve`, { method: "POST" }),

  sessionRegenerate: (sessionId: string, feedback: string) =>
    request<SessionOut>(`/sessions/${sessionId}/regenerate`, {
      method: "POST",
      body: JSON.stringify({ feedback }),
    }),

  sessionReject: (sessionId: string) =>
    request<SessionOut>(`/sessions/${sessionId}/reject`, { method: "POST" }),

  sessionRollback: (sessionId: string) =>
    request<SessionRollbackResult>(`/sessions/${sessionId}/rollback`, {
      method: "POST",
    }),

  sessionEvents: (sessionId: string) =>
    request<SessionEventOut[]>(`/sessions/${sessionId}/events`),

  sessionDownloadEventCsv: async (sessionId: string, eventId: string) => {
    const auth = await getAuthHeaders()
    const res = await fetch(
      `${BASE}/sessions/${sessionId}/events/${eventId}/csv`,
      { headers: { ...auth } },
    )
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.blob()
  },

  // ─── Customer search (v5) ─────────────────────────────────────────────
  customerSearch: (q: string) =>
    request<
      {
        customer_id: string
        customer_name: string
        matches: {
          scope_id: string
          record_id: string
          primitive_name: string
          primitive_title: string
          value: string
          match_type: "value" | "mapped_id"
        }[]
      }[]
    >(`/scopes/customer/search?q=${encodeURIComponent(q)}`),

  // ─── Integration queue (v5) ────────────────────────────────────────────
  integrationQueueList: (apId: string) =>
    request<
      {
        id: string
        ap_id: string
        label: string
        source: string
        search_query: string
        note: string | null
        created_at: string
        accepted_at: string | null
        rejected_at: string | null
      }[]
    >(`/ap/${apId}/queue`),

  integrationQueueCreate: (
    apId: string,
    body: {
      label: string
      search_query: string
      source?: string
      note?: string
    },
  ) =>
    request<{
      id: string
      ap_id: string
      label: string
      source: string
      search_query: string
      note: string | null
      created_at: string
      accepted_at: string | null
      rejected_at: string | null
    }>(`/ap/${apId}/queue`, { method: "POST", body: JSON.stringify(body) }),

  integrationQueueAccept: (apId: string, itemId: string) =>
    request<{
      item: {
        id: string
        ap_id: string
        label: string
        source: string
        search_query: string
        note: string | null
        created_at: string
        accepted_at: string | null
        rejected_at: string | null
      }
      customer_ids: string[]
    }>(`/ap/${apId}/queue/${itemId}/accept`, { method: "POST", body: "{}" }),

  integrationQueueReject: (apId: string, itemId: string) =>
    request<void>(`/ap/${apId}/queue/${itemId}`, { method: "DELETE" }),

  integrationQueueEmailSimulate: (
    apId: string,
    body: { from_address: string; subject: string; body: string },
  ) =>
    request<{ id: string }>(`/ap/${apId}/queue/email-simulate`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  integrationQueueWebhookSimulate: (
    apId: string,
    body: { filename: string; payload: string },
  ) =>
    request<{ id: string }>(`/ap/${apId}/queue/webhook-simulate`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ─── Freshness partition (v5) ──────────────────────────────────────────
  freshnessPartition: (threshold: string) =>
    request<{
      fresh_record_ids: string[]
      stale_record_ids: string[]
      stalest_relation_per_record: Record<
        string,
        {
          scope_id: string
          record_id: string
          updated_at: string | null
          demotion_reason?: string
          demotion_source_ref?: {
            monitor_ap_id?: string
            finding_id?: string
          } | null
        }
      >
      latest_chain_update_per_record?: Record<string, string>
      demotions?: Record<
        string,
        {
          reason: string
          source_ref: { monitor_ap_id?: string; finding_id?: string } | null
          demotion_id: string
        }
      >
    }>(`/scopes/customer/freshness?threshold=${encodeURIComponent(threshold)}`),

  clearDemotions: () =>
    request<{ cleared: number }>("/ap/customer-conformity/demotions/clear", {
      method: "POST",
      body: "{}",
    }),

  // ─── Supplier search ──────────────────────────────────────────────────
  // Mirror of customerSearch anchored on supplier — walks supplier →
  // component → substance and returns suppliers with at least one match
  // anywhere in the chain. Used by the Lieferanten-Abfragen AP.
  supplierSearch: (q: string) =>
    request<
      {
        supplier_id: string
        supplier_name: string
        matches: {
          scope_id: string
          record_id: string
          primitive_name: string
          primitive_title: string
          value: string
          match_type: "value" | "mapped_id"
        }[]
      }[]
    >(`/scopes/supplier/search?q=${encodeURIComponent(q)}`),

  // Supplier-side freshness mirror (used by the Lieferanten-Abfragen AP).
  // For the exporter, *stale* suppliers are the ones the AP wants to surface
  // as Bereit (those need a fresh fragebogen). Shape matches freshnessPartition.
  supplierFreshnessPartition: (threshold: string) =>
    request<{
      fresh_record_ids: string[]
      stale_record_ids: string[]
      stalest_relation_per_record: Record<
        string,
        {
          scope_id: string
          record_id: string
          updated_at: string | null
        }
      >
      latest_chain_update_per_record?: Record<string, string>
      pending_inaktiv_record_ids: string[]
      pending_reason_per_record: Record<
        string,
        {
          source_scope_id: string
          source_record_id: string
          source_record_name: string
          ap_id: string
        }
      >
    }>(`/scopes/supplier/freshness?threshold=${encodeURIComponent(threshold)}`),

  // ─── AP partition (v4.1) — unified Ausstehend / Bereit / Inaktiv ──────
  apPartition: (apId: string, threshold?: string, since?: string) => {
    const params = new URLSearchParams()
    if (threshold) params.set("threshold", threshold)
    if (since) params.set("since", since)
    const qs = params.toString()
    return request<{
      ap_id: string
      threshold: string | null
      since: string | null
      ausstehend: Array<{
        id: string
        record_id: string
        source:
          | "monitor"
          | "cross_ap"
          | "email"
          | "webhook"
          | "user_manual"
          | "auto_reminder"
        reason: string
        priority: "high" | "normal"
        parameters: Record<string, unknown>
        source_ref: Record<string, unknown>
        created_at: string
        current_op_state:
          | "waiting"
          | "due"
          | "answered_received"
          | "answered_ok"
          | "answered_followup"
          | "closed"
          | null
        current_op_id: string | null
        current_op_due_at: string | null
        current_op_channel: "download" | "email" | null
        current_op_is_mock: boolean | null
      }>
      bereit: {
        highlighted_record_ids: string[]
        other_record_ids: string[]
      }
      inaktiv: Array<{ record_id: string; reason: string }>
    }>(`/ap/${apId}/partition${qs ? `?${qs}` : ""}`)
  },

  // ─── AP notifications summary (v4.1) ──────────────────────────────────
  apNotificationsSummary: (threshold?: string) => {
    const params = new URLSearchParams()
    if (threshold) params.set("threshold", threshold)
    const qs = params.toString()
    return request<{
      aps: Record<
        string,
        {
          ausstehend_count: number
          unread_count: number
          bereit_new_count: number
          last_changed_at: string | null
          last_opened_at: string | null
        }
      >
    }>(`/ap/notifications/summary${qs ? `?${qs}` : ""}`)
  },

  // Bump last_opened_at for an AP — resets the unread badge to 0.
  // QMS 4.1 launch: BE endpoint not implemented (ap_settings.last_opened_at
  // column needs a multi-tenant-aware schema rev). Stubbed as a no-op so the
  // FE compiles and badge resets are silently dropped until Phase 3.
  markApOpened: (apId: string) =>
    Promise.resolve({ ap_id: apId, last_opened_at: new Date().toISOString() }),

  // ─── Exporter template (v5) ────────────────────────────────────────────
  exporterTemplateGet: (apId: string) =>
    request<{
      id: string
      ap_id: string
      name: string
      html_source: string
      column_mapping: Record<string, string>
      seed_version: string
      updated_at: string
    }>(`/ap/${apId}/template`),

  exporterTemplatePatch: (
    apId: string,
    body: {
      html_source?: string
      column_mapping?: Record<string, string>
      name?: string
    },
  ) =>
    request<{
      id: string
      ap_id: string
      name: string
      html_source: string
      column_mapping: Record<string, string>
      seed_version: string
      updated_at: string
    }>(`/ap/${apId}/template`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  exporterTemplateReset: (apId: string) =>
    request<{
      id: string
      ap_id: string
      name: string
      html_source: string
      column_mapping: Record<string, string>
      seed_version: string
      updated_at: string
    }>(`/ap/${apId}/template/reset`, { method: "POST", body: "{}" }),

  exporterTemplateRender: async (
    apId: string,
    recordId: string,
    diff?: Record<string, Record<string, Record<string, string>>>,
  ) => {
    const auth = await getAuthHeaders()
    const res = await fetch(`${BASE}/ap/${apId}/template/render`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ record_id: recordId, diff }),
    })
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.text()
  },

  exporterTemplateExport: async (
    apId: string,
    recordId: string,
    diff?: Record<string, Record<string, Record<string, string>>>,
  ) => {
    const auth = await getAuthHeaders()
    const res = await fetch(`${BASE}/ap/${apId}/template/export`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ record_id: recordId, diff }),
    })
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)
    return res.text()
  },

  // ─── Monitor AP (v6) ──────────────────────────────────────────────────

  monitorListFindings: (apId: string, state: "open" | "done" = "open") =>
    request<MonitorFindingOut[]>(`/ap/${apId}/monitor/findings?state=${state}`),

  monitorGetFinding: (apId: string, findingId: string) =>
    request<MonitorFindingOut>(`/ap/${apId}/monitor/findings/${findingId}`),

  monitorEvaluate: (apId: string) =>
    request<{ created: number; superseded: number; flagged: string[] }>(
      `/ap/${apId}/monitor/evaluate`,
      { method: "POST", body: "{}" },
    ),

  monitorApplyRecommendation: (
    apId: string,
    findingId: string,
    recId: string,
  ) =>
    request<MonitorFindingOut>(
      `/ap/${apId}/monitor/findings/${findingId}/recommendations/${recId}/apply`,
      { method: "POST", body: "{}" },
    ),

  monitorDismissRecommendation: (
    apId: string,
    findingId: string,
    recId: string,
  ) =>
    request<MonitorFindingOut>(
      `/ap/${apId}/monitor/findings/${findingId}/recommendations/${recId}/dismiss`,
      { method: "POST", body: "{}" },
    ),

  monitorTestTrigger: (apId: string) =>
    request<MonitorFindingOut[]>(`/ap/${apId}/monitor/test-trigger`, {
      method: "POST",
      body: "{}",
    }),

  monitorStats: (apId: string) =>
    request<{ open_count: number; applied_last_30_days: number }>(
      `/ap/${apId}/monitor/stats`,
    ),

  // ─── AP Inbox (v6) ────────────────────────────────────────────────────

  apInboxPost: (
    apId: string,
    token: string,
    body: {
      action: string
      payload: Record<string, unknown>
      source_ref?: Record<string, unknown>
    },
  ) =>
    request<{ ok: boolean } & Record<string, unknown>>(
      `/ap/${apId}/inbox?token=${encodeURIComponent(token)}`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // ─── Exporter operations (Versand-Verlauf, server-persisted) ──────────
  exporterOperationsList: (apId: string) =>
    request<ExportOperationOut[]>(`/ap/${apId}/exporter/operations`),

  exporterOperationsCreate: (apId: string, body: ExportOperationCreateIn) =>
    request<ExportOperationOut>(`/ap/${apId}/exporter/operations`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  exporterOperationsReply: (
    apId: string,
    opId: string,
    body: ExportOperationReplyIn,
  ) =>
    request<ExportOperationOut>(
      `/ap/${apId}/exporter/operations/${opId}/reply`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    ),

  exporterOperationsRemind: (apId: string, opId: string) =>
    request<ExportOperationOut>(
      `/ap/${apId}/exporter/operations/${opId}/remind`,
      {
        method: "POST",
        body: "{}",
      },
    ),

  exporterOperationsSimulateReply: (
    apId: string,
    opId: string,
    body?: {
      from_address?: string
      subject?: string
      body_text?: string
      record_name?: string
    },
  ) =>
    request<ExportOperationOut>(
      `/ap/${apId}/exporter/operations/${opId}/simulate-reply`,
      { method: "POST", body: JSON.stringify(body ?? {}) },
    ),

  exporterOperationsAttachmentUrl: (apId: string, opId: string) =>
    `${BASE}/ap/${apId}/exporter/operations/${opId}/response-attachment`,

  exporterOperationsResponseAttachmentUrl: (
    apId: string,
    opId: string,
    attachmentId: string,
  ) =>
    `${BASE}/ap/${apId}/exporter/operations/${opId}/response-attachments/${attachmentId}`,

  exporterOperationsForwardToImporter: (
    apId: string,
    opId: string,
    body: { target_ap_id: string },
  ) =>
    request<ForwardToImporterOut>(
      `/ap/${apId}/exporter/operations/${opId}/forward-to-importer`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  exporterIntegrationStatus: () =>
    request<{
      mode: "real" | "mock"
      inbound_domain?: string | null
      from_email?: string | null
    }>(`/exporter/integration-status`),
}

// ─── Exporter operation types (shared with the cockpit store) ─────────────────

export interface ResponseAttachmentOut {
  id: string
  filename: string
  content_type: string
  size_bytes: number
}

export interface ExportOperationOut {
  id: string
  ap_id: string
  record_id: string
  template_ids: string[]
  channel: "download" | "email"
  state:
    | "waiting"
    | "due"
    | "answered_received"
    | "answered_ok"
    | "answered_followup"
    | "closed"
  created_at: string
  created_by: string
  email_to?: string | null
  email_subject?: string | null
  email_body?: string | null
  due_at?: string | null
  response_received_at?: string | null
  response_from?: string | null
  response_subject?: string | null
  response_body_text?: string | null
  response_body_html?: string | null
  response_summary?: string | null
  response_file_name?: string | null
  response_file_content_type?: string | null
  has_response_attachment?: boolean
  response_attachments?: ResponseAttachmentOut[]
  follow_up_of?: string | null
  provider_message_id?: string | null
  is_mock?: boolean
}

export interface ExportOperationCreateIn {
  record_id: string
  template_ids: string[]
  channel: "download" | "email"
  email_to?: string
  email_subject?: string
  email_body?: string
  follow_up_days?: number
  follow_up_of?: string
}

export interface ExportOperationReplyIn {
  kind: "ok" | "followup"
  reason?: string
  file_name?: string
}

export interface ForwardToImporterOut {
  operation: ExportOperationOut
  pending_document_id: string
  target_record_id: string | null
  pending_document_ids?: string[]
}

// ─── Monitor types (shared) ───────────────────────────────────────────────

export interface MonitorRecommendationOut {
  id: string
  target_ap_id: string
  target_action: string
  target_record_ids: string[]
  payload_extras: Record<string, unknown>
  reason: string
  state: "pending" | "applied" | "dismissed"
  applied_at: string | null
}

export interface MonitorFindingOut {
  id: string
  monitor_ap_id: string
  flagged_scope_id: string
  flagged_record_id: string
  summary: string
  evidence: {
    kind: string
    citation: string
    source_url: string
    effective_date: string
    substance_name?: string
    component_name?: string
    narrative?: string
    why_relevant?: string
    affected_supplier_names?: string[]
  }
  recommendations: MonitorRecommendationOut[]
  state:
    | "pending"
    | "partially_applied"
    | "applied"
    | "dismissed"
    | "mixed_resolved"
    | "superseded"
  created_at: string
  resolved_at: string | null
}
